import React from 'react';
import {useNavigate} from 'react-router-dom';
import {AppBar, Avatar, Box, Button, Divider, Drawer, IconButton, List, ListItem, ListItemIcon, ListItemText, Menu, MenuItem, Toolbar, Typography, useMediaQuery, useTheme,} from '@mui/material';
import {AccountCircle, Business, Dashboard as DashboardIcon, Event, LocalFireDepartment, Logout, Menu as MenuIcon, People, PeopleAlt,} from '@mui/icons-material';
import {useAuth} from '../../context/AuthContext';
import '../../assets/styles/components/Navbar.scss';

const Navbar = () => {
    const navigate = useNavigate();
    const {user, logout, isAdmin, isSupervisor} = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMobileMenuToggle = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const handleMobileMenuClose = () => {
        setMobileMenuOpen(false);
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
        handleClose();
        handleMobileMenuClose();
    };

    const handleNavigation = (path) => {
        navigate(path);
        handleMobileMenuClose();
    };

    const menuItems = [
        {text: 'Dashboard', icon: <DashboardIcon/>, path: '/dashboard', show: true},
        {text: 'Übungsdienste', icon: <Event/>, path: '/events', show: isSupervisor()},
        {text: 'Mitglieder', icon: <People/>, path: '/members', show: isSupervisor()},
        {text: 'Benutzer', icon: <PeopleAlt/>, path: '/admin/users', show: isAdmin()},
        {text: 'Einheiten', icon: <Business/>, path: '/admin/units', show: isAdmin()},
    ];

    return (
        <AppBar position="sticky" className="navbar">
            <Toolbar>
                <Box className="navbar-brand" onClick={() => handleNavigation('/dashboard')} sx={{cursor: 'pointer'}}>
                    <LocalFireDepartment className="brand-icon"/>
                    <Box sx={{display: {xs: 'none', sm: 'block'}}}>
                        <Typography variant="h6" className="brand-title">
                            Firebook
                        </Typography>
                        <Typography variant="caption" className="brand-subtitle">
                            Bedburg Feuerwehr
                        </Typography>
                    </Box>
                    <Box sx={{display: {xs: 'block', sm: 'none'}}}>
                        <Typography variant="h6" className="brand-title" sx={{fontSize: '1rem'}}>
                            FB Bedburg
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{flexGrow: 1}}/>

                {isMobile ? (
                    <>
                        <IconButton
                            size="large"
                            onClick={handleMobileMenuToggle}
                            color="inherit"
                            edge="end"
                        >
                            <MenuIcon/>
                        </IconButton>
                        <Drawer
                            anchor="right"
                            open={mobileMenuOpen}
                            onClose={handleMobileMenuClose}
                            slotProps={{
                                paper: {sx: {width: 280}}
                            }}
                        >
                            <Box sx={{p: 2}}>
                                <Box sx={{mb: 2, textAlign: 'center'}}>
                                    <Avatar sx={{width: 60, height: 60, margin: '0 auto', mb: 1, bgcolor: 'primary.main'}}>
                                        {user?.firstName?.[0]?.toUpperCase() || 'U'}
                                    </Avatar>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {user?.firstName} {user?.lastName}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {user?.email}
                                    </Typography>
                                </Box>
                                <Divider sx={{mb: 2}}/>
                                <List>
                                    {menuItems.filter(item => item.show).map((item) => (
                                        <ListItem
                                            button
                                            key={item.text}
                                            onClick={() => handleNavigation(item.path)}
                                            sx={{borderRadius: 1, mb: 0.5}}
                                        >
                                            <ListItemIcon>{item.icon}</ListItemIcon>
                                            <ListItemText primary={item.text}/>
                                        </ListItem>
                                    ))}
                                    <Divider sx={{my: 2}}/>
                                    <ListItem
                                        button
                                        onClick={() => handleNavigation('/profile')}
                                        sx={{borderRadius: 1, mb: 0.5}}
                                    >
                                        <ListItemIcon><AccountCircle/></ListItemIcon>
                                        <ListItemText primary="Profil"/>
                                    </ListItem>
                                    <ListItem
                                        button
                                        onClick={handleLogout}
                                        sx={{borderRadius: 1, color: 'error.main'}}
                                    >
                                        <ListItemIcon><Logout color="error"/></ListItemIcon>
                                        <ListItemText primary="Abmelden"/>
                                    </ListItem>
                                </List>
                            </Box>
                        </Drawer>
                    </>
                ) : (
                    <Box className="navbar-menu">
                        <Button
                            color="inherit"
                            startIcon={<DashboardIcon/>}
                            onClick={() => navigate('/dashboard')}
                            className="nav-button"
                        >
                            Dashboard
                        </Button>

                        <Button
                            color="inherit"
                            startIcon={<Event/>}
                            onClick={() => navigate('/events')}
                            className="nav-button"
                        >
                            Übungsdienste
                        </Button>

                        {isSupervisor() && (
                            <Button
                                color="inherit"
                                startIcon={<People/>}
                                onClick={() => navigate('/members')}
                                className="nav-button"
                            >
                                Mitglieder
                            </Button>
                        )}

                        {isAdmin() && (
                            <>
                                <Button
                                    color="inherit"
                                    startIcon={<PeopleAlt/>}
                                    onClick={() => navigate('/admin/users')}
                                    className="nav-button"
                                >
                                    Benutzer
                                </Button>
                                <Button
                                    color="inherit"
                                    startIcon={<Business/>}
                                    onClick={() => navigate('/admin/units')}
                                    className="nav-button"
                                >
                                    Einheiten
                                </Button>
                            </>
                        )}

                        <IconButton
                            size="large"
                            onClick={handleMenu}
                            color="inherit"
                            className="user-button"
                        >
                            <Avatar className="user-avatar">
                                {user?.firstName?.[0]?.toUpperCase() || 'U'}
                            </Avatar>
                        </IconButton>

                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleClose}
                            className="user-menu"
                        >
                            <Box className="menu-header">
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {user?.firstName} {user?.lastName}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {user?.email}
                                </Typography>
                            </Box>
                            <Divider/>
                            <MenuItem onClick={() => {
                                navigate('/profile');
                                handleClose();
                            }}>
                                <AccountCircle fontSize="small" style={{marginRight: 8}}/>
                                Profil
                            </MenuItem>
                            <MenuItem onClick={handleLogout}>
                                <Logout fontSize="small" style={{marginRight: 8}}/>
                                Abmelden
                            </MenuItem>
                        </Menu>
                    </Box>
                )}
            </Toolbar>
        </AppBar>
    );
};

export default Navbar;
